/**
 * MakeID Printer Integration for ArgoCamp
 * 
 * This module provides print functionality using the ArgoCamp Printer Android app
 * as a bridge to MakeID Bluetooth printers.
 * 
 * Usage:
 * ```typescript
 * import { printAssetLabel, canPrint } from './print';
 * 
 * if (canPrint()) {
 *   printAssetLabel({
 *     title: 'My Asset',
 *     code: 'AST-001',
 *     qrUrl: 'https://argocamp.app/assets/1',
 *     location: 'Warehouse A'
 *   });
 * }
 * ```
 */

// Deep link scheme for ArgoCamp Printer app
const PRINT_SCHEME = 'argocamp';
const PRINT_HOST = 'print';

/**
 * Label types supported by the printer app
 */
export type LabelType = 'asset_label' | 'ticket_label' | 'simple_qr' | 'location_label';

/**
 * Standard label sizes (in mm)
 */
export const LABEL_SIZES = {
  small: { width: 40, height: 20 },
  medium: { width: 50, height: 25 },
  large: { width: 70, height: 30 },
  cable: { width: 38, height: 25 },
} as const;

/**
 * Base print data interface
 */
interface BasePrintData {
  type: LabelType;
  width?: number;
  height?: number;
}

/**
 * Asset label print data
 */
export interface AssetLabelData extends BasePrintData {
  type: 'asset_label';
  code: string;
  qrUrl: string;
  name?: string;
  brand?: string;
  model?: string;
  serialNumber?: string;
  purchaseDate?: string;
}

/**
 * Ticket label print data
 */
export interface TicketLabelData extends BasePrintData {
  type: 'ticket_label';
  ticketNumber: string;
  title: string;
  qrUrl: string;
}

/**
 * Simple QR label print data
 */
export interface SimpleQrLabelData extends BasePrintData {
  type: 'simple_qr';
  qrContent: string;
  text?: string;
}

/**
 * Location label print data
 */
export interface LocationLabelData extends BasePrintData {
  type: 'location_label';
  code: string;
  qrUrl: string;
  name: string;
  locationType?: string;
  capacity?: string;
  hasChildren?: boolean;
}

export type PrintLabelData = 
  | AssetLabelData 
  | TicketLabelData 
  | SimpleQrLabelData 
  | LocationLabelData;

/**
 * Check if the current device is a mobile device
 */
export function isMobile(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/**
 * Check if the current device supports printing (Android only)
 */
export function canPrint(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android/i.test(navigator.userAgent);
}

/**
 * Check if we're running as a PWA (installed)
 */
export function isPWA(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches;
}

/**
 * Build deep link URL for printing
 */
function buildPrintUrl(data: PrintLabelData): string {
  const params = new URLSearchParams();
  
  params.set('type', data.type);
  
  if (data.width) params.set('width', data.width.toString());
  if (data.height) params.set('height', data.height.toString());

  switch (data.type) {
    case 'asset_label':
      params.set('code', data.code);
      params.set('qr', data.qrUrl);
      if (data.name) params.set('name', data.name);
      if (data.brand) params.set('brand', data.brand);
      if (data.model) params.set('model', data.model);
      if (data.serialNumber) params.set('serial', data.serialNumber);
      if (data.purchaseDate) params.set('purchaseDate', data.purchaseDate);
      break;
    
    case 'ticket_label':
      params.set('title', data.title);
      params.set('code', data.ticketNumber);
      params.set('qr', data.qrUrl);
      break;
    
    case 'simple_qr':
      params.set('qr', data.qrContent);
      if (data.text) params.set('title', data.text);
      break;
    
    case 'location_label':
      params.set('code', data.code);
      params.set('qr', data.qrUrl);
      params.set('name', data.name);
      if (data.locationType) params.set('locationType', data.locationType);
      if (data.capacity) params.set('capacity', data.capacity);
      if (data.hasChildren !== undefined) params.set('hasChildren', data.hasChildren ? 'true' : 'false');
      break;
  }

  return `${PRINT_SCHEME}://${PRINT_HOST}?${params.toString()}`;
}

/**
 * Open the print app with the given label data
 */
export function print(data: PrintLabelData): boolean {
  if (!canPrint()) {
    console.warn('Printing is only supported on Android devices');
    return false;
  }

  const url = buildPrintUrl(data);
  console.log('Opening print URL:', url);
  
  // Navigate to deep link
  window.location.href = url;
  
  return true;
}

/**
 * Print an asset label
 */
export function printAssetLabel(options: {
  code: string;
  qrUrl: string;
  name?: string;
  brand?: string;
  model?: string;
  serialNumber?: string;
  purchaseDate?: string;
  size?: keyof typeof LABEL_SIZES;
}): boolean {
  const size = LABEL_SIZES[options.size || 'medium'];
  
  return print({
    type: 'asset_label',
    code: options.code,
    qrUrl: options.qrUrl,
    name: options.name,
    brand: options.brand,
    model: options.model,
    serialNumber: options.serialNumber,
    purchaseDate: options.purchaseDate,
    width: size.width,
    height: size.height,
  });
}

/**
 * Print a ticket label
 */
export function printTicketLabel(options: {
  ticketNumber: string;
  title: string;
  qrUrl: string;
  size?: keyof typeof LABEL_SIZES;
}): boolean {
  const size = LABEL_SIZES[options.size || 'medium'];
  
  return print({
    type: 'ticket_label',
    ticketNumber: options.ticketNumber,
    title: options.title,
    qrUrl: options.qrUrl,
    width: size.width,
    height: size.height,
  });
}

/**
 * Print a simple QR code label
 */
export function printQrLabel(options: {
  qrContent: string;
  text?: string;
  size?: keyof typeof LABEL_SIZES;
}): boolean {
  const size = LABEL_SIZES[options.size || 'small'];
  
  return print({
    type: 'simple_qr',
    qrContent: options.qrContent,
    text: options.text,
    width: size.width,
    height: size.height,
  });
}

/**
 * Print a location label
 */
export function printLocationLabel(options: {
  code: string;
  qrUrl: string;
  name: string;
  locationType?: string;
  capacity?: string;
  hasChildren?: boolean;
  size?: keyof typeof LABEL_SIZES;
}): boolean {
  const size = LABEL_SIZES[options.size || 'medium'];
  
  return print({
    type: 'location_label',
    code: options.code,
    qrUrl: options.qrUrl,
    name: options.name,
    locationType: options.locationType,
    capacity: options.capacity,
    hasChildren: options.hasChildren,
    width: size.width,
    height: size.height,
  });
}

/**
 * Get the download URL for the ArgoCamp Printer app
 * (Update this when the app is published)
 */
export function getPrinterAppDownloadUrl(): string {
  // TODO: Replace with actual Play Store or direct APK URL
  return 'https://argocamp.app/download/printer-app.apk';
}

